import React, { useCallback, useEffect, useState } from 'react';

import { styled } from '@compiled/react';
import { bind, type UnbindFn } from 'bind-event-listener';
import ReactDOM from 'react-dom';

import { getDocument } from '@atlaskit/browser-apis';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';

const OFFSET_TO_THE_RIGHT_OF_ITEM = 30;
const OFFSET_FROM_BOTTOM_OF_VIEWPORT = 5;

type PopupPositionType = {
	height: number;
	left: number;
	top: number;
};

type PopupWrapperProps = {
	isVisible: boolean;
	left: number;
	top: number;
};

const ESCAPE_KEY = 'Escape';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const PopupWrapper = styled.div<PopupWrapperProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	top: `${(props: PopupWrapperProps) => props.top}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	left: `${(props: PopupWrapperProps) => props.left}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	visibility: (props) => (props.isVisible ? 'visible' : 'hidden'),
	position: 'fixed',
	zIndex: layers.tooltip(),
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.large'),
});

export type CardProps = {
	customStyles?: object;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Card = styled.div<CardProps>({
	position: 'relative',
	backgroundColor: token('elevation.surface'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	width: '400px',
});

export interface InvitedAppPopupProps {
	children: React.ReactNode;
	onMouseEnterPopup: () => void;
	onMouseLeavePopup: () => void;
	trigger: React.MutableRefObject<HTMLAnchorElement | null>;
}

export const InvitedAppPopup = ({
	trigger,
	onMouseEnterPopup,
	onMouseLeavePopup,
	children,
}: InvitedAppPopupProps) => {
	const [popupPosition, setPopupPosition] = useState<PopupPositionType>({
		top: -1000,
		left: -1000,
		height: 0,
	});
	const [isVisible, setIsVisible] = useState<boolean>(false);

	const popupContainerCallbackRef = useCallback(
		(element: HTMLDivElement | null) => {
			const document = getDocument();
			if (element && trigger.current && document) {
				const popupClientRect = element.getBoundingClientRect();
				const triggerClientRect = trigger.current?.getBoundingClientRect();

				let top = triggerClientRect.top - popupClientRect.height / 2 + triggerClientRect.height / 2;

				// Check if the rendered popup would be cut off at the bottom and reposition upwards
				const bottom = top + popupClientRect.height;
				const boundaryY = document.documentElement.clientHeight;

				if (bottom >= boundaryY) {
					top = top - (bottom - boundaryY) - OFFSET_FROM_BOTTOM_OF_VIEWPORT;
				}

				setPopupPosition({
					top,
					left: triggerClientRect.right + OFFSET_TO_THE_RIGHT_OF_ITEM,
					height: popupClientRect.height,
				});

				setIsVisible(true);
			}
		},
		[trigger],
	);

	const handleEscKeyPress = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === ESCAPE_KEY) {
				onMouseLeavePopup();
			}
		},
		[onMouseLeavePopup],
	);

	useEffect(() => {
		// Ensure the component is cleaned up when pressing escape key
		const document = getDocument();
		const unbind: UnbindFn = document
			? bind(document, {
					type: 'keydown',
					listener: handleEscKeyPress,
				})
			: () => {};

		return () => unbind();
	}, [handleEscKeyPress]);

	return trigger.current
		? ReactDOM.createPortal(
				<div
					onMouseEnter={onMouseEnterPopup}
					onFocus={onMouseEnterPopup}
					onMouseLeave={onMouseLeavePopup}
					onBlur={onMouseLeavePopup}
				>
					<PopupWrapper
						data-testid="invited-app-popup"
						role="tooltip"
						top={popupPosition.top}
						left={popupPosition.left}
						isVisible={isVisible}
					>
						<Card ref={popupContainerCallbackRef}>{children}</Card>
					</PopupWrapper>
				</div>,
				document.body,
			)
		: null;
};
